import fetchLoyaltyEsimUpsellCatalog, { getLoyaltyEsimUpsellCatalogKey } from 'actions/loyalty/fetchLoyaltyEsimUpsellCatalog'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isRecord } from 'lib/object/objectUtils'
import { buildEsimUpsellPurchasableTickets } from 'loyalty/lib/esims/buildEsimUpsellPurchasableTickets'
import { useEffect, useMemo } from 'react'

interface UseEsimUpsellCatalogOptions {
  countryCode?: string
  orderId?: string
  /** Required for cache keying; fetch is skipped while undefined. */
  currentPackageTicketId: App.LoyaltyEsimUpsellTicketId | undefined
  enabled?: boolean
}

export default function useEsimUpsellCatalog({
  countryCode,
  orderId,
  currentPackageTicketId,
  enabled = true,
}: UseEsimUpsellCatalogOptions): {
  catalog: App.LoyaltyEsimUpsellCatalog | undefined
  isLoading: boolean
  hasPurchasableUpgrades: boolean
  canShowUpsellCatalog: boolean
} {
  const dispatch = useAppDispatch()
  const customerId = useAppSelector(state => state.auth.account.memberId)
  const currencyCode = useAppSelector(state => state.geo.currentCurrency)
  const payload = useLabRemoteConfig(LabRemoteConfigKeys.loyaltyEsimUpsell)
  const isEsimUpsellEnabled = useMemo(() => {
    const { value } = payload ?? {}
    return isRecord(value) && value.enabled === true
  }, [payload])
  const shouldFetch = enabled
    && isEsimUpsellEnabled
    && !!countryCode
    && !!customerId
    && !!currentPackageTicketId

  const catalogKey = countryCode && customerId && currentPackageTicketId
    ? getLoyaltyEsimUpsellCatalogKey({
        countryCode,
        currencyCode,
        customerId,
        currentPackageTicketId,
        orderId,
      })
    : undefined

  const catalogEntry = useAppSelector(state => (
    catalogKey ? state.loyalty.esimUpsellCatalogs[catalogKey] : undefined
  ))

  useEffect(() => {
    if (shouldFetch && countryCode && currentPackageTicketId) {
      dispatch(fetchLoyaltyEsimUpsellCatalog({
        countryCode,
        currentPackageTicketId,
        orderId,
      }))
    }
  }, [dispatch, countryCode, currencyCode, customerId, currentPackageTicketId, orderId, shouldFetch])

  const catalog = shouldFetch ? catalogEntry?.data : undefined

  const hasPurchasableUpgrades = useMemo(() => {
    if (!catalog) return false

    return buildEsimUpsellPurchasableTickets(catalog, currentPackageTicketId).length > 0
  }, [catalog, currentPackageTicketId])

  const isLoading = shouldFetch && (!catalogEntry || !!catalogEntry.fetching)

  return {
    catalog,
    isLoading,
    hasPurchasableUpgrades,
    canShowUpsellCatalog: !isLoading && !!catalog && hasPurchasableUpgrades,
  }
}
